import type {Icon} from '@primer/octicons-react'
import {CopyIcon} from '@primer/octicons-react'
import type {ButtonProps, IconButtonProps, TooltipProps} from '@primer/react'
import {IconButton, Tooltip, VisuallyHidden} from '@primer/react'
import {Announce} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {useId} from 'react'

import {useCopyToClipboard} from './use-copy-to-clipboard'

import styles from './CopyToClipboardIconButton.module.css'

type VariantType = NonNullable<ButtonProps['variant']>

export interface CopyToClipboardIconButtonProps extends Omit<
  IconButtonProps,
  'icon' | 'onClick' | 'aria-labelledby' | 'aria-label' | 'unsafeDisableTooltip' | 'tooltipDirection'
> {
  /**
   * Octicon that is displayed on the copy button
   * @default CopyIcon
   */
  icon?: Icon
  /**
   * Size of the button, passed to IconButton
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Optional callback that is invoked when the user clicks the copy button
   */
  onCopy?: () => void
  /**
   * Text that will be copied to the clipboard
   */
  textToCopy: string
  /**
   * Props that will be applied to tooltips
   */
  tooltipProps?: Omit<TooltipProps, 'text' | 'aria-label' | 'type' | 'id' | 'aria-hidden'>
  /**
   * Which icon button variant to use
   * @default "invisible"
   */
  variant?: VariantType
  /**
   * Text that will be displayed in the tooltip
   * @default `Copy "${textToCopy}" to clipboard`
   */
  ariaLabel?: string
}

export function CopyToClipboardIconButton({
  icon = CopyIcon,
  size = 'medium',
  onCopy,
  textToCopy,
  tooltipProps,
  variant = 'invisible',
  ariaLabel,
  className,
  disabled,
  ...forwardProps
}: CopyToClipboardIconButtonProps) {
  const {announcement, copied, copyToClipboard, visual} = useCopyToClipboard({
    onCopy,
    textToCopy,
    visual: icon,
  })

  const label = ariaLabel ?? `Copy "${textToCopy}" to clipboard`
  const visibleTooltipText = copied ? 'Copied!' : label

  const sharedProps = {
    size,
    variant,
    onClick: copyToClipboard,
    icon: visual,
    className: clsx(copied ? 'color-fg-success' : undefined, className),
    ...forwardProps,
  }

  const tooltipId = useId()

  // Tooltip isn't allowed to wrap a disabled button
  return disabled ? (
    <IconButton
      {...sharedProps}
      // Without a tooltip we need an explicit label
      aria-label={label}
      disabled
    />
  ) : (
    <>
      {announcement && (
        <VisuallyHidden>
          <Announce>{announcement}</Announce>
        </VisuallyHidden>
      )}
      <Tooltip
        text={visibleTooltipText}
        aria-label={label}
        type="label"
        id={tooltipId}
        // We are adding `aria-hidden="true"` to the tooltip because the button has an accessible name.
        // We don't want to override that when the visible text updates to "Copied!" because
        // our screen reader announcement will let the user know their action was successful in the most
        // consistent way across browsers and assistive technology.
        aria-hidden
        {...tooltipProps}
        className={clsx(styles.tooltip, tooltipProps?.className)}
      >
        <IconButton {...sharedProps} aria-labelledby={tooltipId} />
      </Tooltip>
    </>
  )
}
